export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      pricing: 'Pricing',
      generateCV: 'Generate CV',
      support: 'Support',
      getStarted: 'Get Started',
      login: 'Login',
      logout: 'Logout',
    },
    // Hero Section
    hero: {
      title: 'Create Your Professional CV',
      titleHighlight: 'in Minutes',
      subtitle: 'Build stunning, ATS-friendly resumes that stand out. Simple, affordable, and multilingual.',
      cta: 'Create My CV Free',
      ctaSecondary: 'See Examples',
      stats: {
        cvs: 'CVs Created',
        satisfaction: 'Satisfaction',
        languages: 'Languages',
      },
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Everything you need to land your dream job',
      cv: {
        title: 'CV Generator',
        description: 'Create a professional two-column CV with live preview. Choose from multiple themes and export as PDF.',
      },
      coverLetter: {
        title: 'Cover Letter',
        description: 'Generate a personalized cover letter based on your CV data. Perfectly formatted and ready to send.',
      },
      linkedin: {
        title: 'LinkedIn Profile',
        description: 'Convert your CV into a LinkedIn-optimized profile with headline, summary, and formatted experience.',
      },
      jobAdapt: {
        title: 'Job Adaptation',
        description: 'Paste a job offer and get your CV and cover letter automatically adapted to match the requirements.',
      },
    },
    // Pricing
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose individual services or subscribe for full access.',
      free: {
        title: 'Free Preview',
        price: '0€',
        description: 'Try before you buy',
        features: ['Full CV preview', 'Live editing', 'Theme selection', '1 free test generation'],
        cta: 'Start Free',
      },
      cvDownload: {
        title: 'CV Download',
        price: '2€',
        description: 'Download your professional CV',
        features: ['PDF export', 'A4 format', 'ATS-friendly', 'Print ready'],
        cta: 'Download CV',
      },
      coverLetter: {
        title: 'Cover Letter',
        price: '1€',
        description: 'Professional cover letter',
        features: ['Auto-generated from CV', 'Professional format', 'Customizable', 'PDF download'],
        cta: 'Get Cover Letter',
      },
      pack: {
        title: 'CV + Cover Letter',
        price: '3€',
        description: 'Complete application pack',
        features: ['CV download', 'Cover letter', 'Matching design', 'Best value'],
        cta: 'Get Pack',
        popular: 'Popular',
      },
      linkedin: {
        title: 'LinkedIn Conversion',
        price: '2€',
        description: 'LinkedIn-ready profile',
        features: ['Headline generator', 'Summary text', 'Experience format', 'Copy-paste ready'],
        cta: 'Convert to LinkedIn',
      },
      jobAdapt: {
        title: 'Job Adaptation',
        price: '5€',
        description: 'CV + Letter for a specific job',
        features: ['Paste job offer', 'Adapted CV', 'Tailored letter', 'Keyword optimization'],
        cta: 'Adapt to Job',
      },
    },
    // CV Builder
    cvBuilder: {
      title: 'Build Your CV',
      preview: 'Preview',
      form: {
        personalInfo: 'Personal Information',
        name: 'Full Name',
        jobTitle: 'Job Title',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        website: 'Website/Portfolio',
        photo: 'Profile Photo',
        uploadPhoto: 'Upload Photo',
        changePhoto: 'Change Photo',
        summary: 'Profile Summary',
        summaryPlaceholder: 'Write a brief professional summary...',
        education: 'Education',
        addEducation: 'Add Education',
        degree: 'Degree',
        institution: 'Institution',
        year: 'Year',
        experience: 'Work Experience',
        addExperience: 'Add Experience',
        position: 'Position',
        company: 'Company',
        period: 'Period',
        description: 'Description',
        skills: 'Skills',
        addSkill: 'Add Skill',
        skillPlaceholder: 'Enter skill and press Enter',
        languages: 'Languages',
        addLanguage: 'Add Language',
        language: 'Language',
        level: 'Level',
        levels: {
          native: 'Native',
          fluent: 'Fluent',
          advanced: 'Advanced',
          intermediate: 'Intermediate',
          basic: 'Basic',
        },
      },
      theme: 'CV Theme',
      themes: {
        blue: 'Professional Blue',
        green: 'Natural Green',
        creative: 'Creative Purple',
      },
      download: 'Download PDF',
      downloadCV: 'Download CV',
      downloadCoverLetter: 'Download Cover Letter',
      generateCoverLetter: 'Generate Cover Letter',
      convertLinkedIn: 'Convert to LinkedIn',
      adaptToJob: 'Adapt to Job Offer',
      freeUsed: 'Free generation used',
      unlockWith: 'Unlock with payment',
    },
    // Cover Letter
    coverLetter: {
      title: 'Cover Letter Generator',
      generated: 'Your Cover Letter',
      intro: 'Dear Hiring Manager,',
      closing: 'Best regards,',
    },
    // LinkedIn
    linkedin: {
      title: 'LinkedIn Profile Converter',
      headline: 'LinkedIn Headline',
      summary: 'Professional Summary',
      experience: 'Experience Format',
      copyAll: 'Copy All',
      copied: 'Copied!',
    },
    // Job Adaptation
    jobAdapt: {
      title: 'Adapt to Job Offer',
      paste: 'Paste Job Description',
      placeholder: 'Paste the job offer text here...',
      analyze: 'Analyze & Adapt',
      adaptedTitle: 'Adapted CV Title',
      adaptedSummary: 'Adapted Summary',
      adaptedLetter: 'Adapted Cover Letter',
    },
    // Support
    support: {
      title: 'Need Help?',
      subtitle: "We're here for you",
      contact: 'Contact Us',
      name: 'Your Name',
      email: 'Email Address',
      subject: 'Subject',
      message: 'Your Message',
      send: 'Send Message',
      sent: 'Message Sent!',
      sentMessage: "We'll get back to you within 24 hours.",
      faq: 'Frequently Asked Questions',
      faqs: [
        {
          q: 'How do I download my CV?',
          a: 'After creating your CV, simply click the "Download PDF" button. A one-time payment of 2€ is required to unlock the download feature.',
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept payments through Stripe and PayPal. Your payment information is secure and encrypted.',
        },
        {
          q: 'Can I edit my CV after creating it?',
          a: 'Yes! You can edit your CV as many times as you want. Your data is saved in your browser. Just visit the Generate CV page to continue editing.',
        },
        {
          q: 'Is my CV ATS-friendly?',
          a: 'Yes, our CV templates are designed to be ATS (Applicant Tracking System) compatible. The clean layout and formatting ensure your CV can be parsed by recruitment software.',
        },
        {
          q: 'How does the cover letter generator work?',
          a: 'The cover letter is automatically generated using the information from your CV. It creates a professional letter tailored to your experience and skills.',
        },
      ],
    },
    // Footer
    footer: {
      description: 'Create professional CVs and job application documents easily. Simple, affordable, multilingual.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: `© ${new Date().getFullYear()} CVLY. All rights reserved.`,
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      required: 'Required',
    },
    // Payment Modal
    payment: {
      title: 'Complete Your Purchase',
      secure: 'Secure payment via',
      payWith: 'Pay with',
      processing: 'Processing...',
      success: 'Payment Successful!',
      successMessage: 'Your download is now unlocked.',
      testMode: 'Test Mode: Click to simulate payment',
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      services: 'Services',
      pricing: 'Tarifs',
      generateCV: 'Créer un CV',
      support: 'Support',
      getStarted: 'Commencer',
      login: 'Connexion',
      logout: 'Déconnexion',
    },
    hero: {
      title: 'Créez Votre CV Professionnel',
      titleHighlight: 'en Minutes',
      subtitle: 'Créez des CV élégants et compatibles ATS qui se démarquent. Simple, abordable et multilingue.',
      cta: 'Créer Mon CV Gratuit',
      ctaSecondary: 'Voir des Exemples',
      stats: {
        cvs: 'CVs Créés',
        satisfaction: 'Satisfaction',
        languages: 'Langues',
      },
    },
    services: {
      title: 'Nos Services',
      subtitle: 'Tout ce dont vous avez besoin pour décrocher votre emploi de rêve',
      cv: {
        title: 'Générateur de CV',
        description: 'Créez un CV professionnel à deux colonnes avec aperçu en direct. Choisissez parmi plusieurs thèmes et exportez en PDF.',
      },
      coverLetter: {
        title: 'Lettre de Motivation',
        description: 'Générez une lettre personnalisée basée sur votre CV. Parfaitement formatée et prête à envoyer.',
      },
      linkedin: {
        title: 'Profil LinkedIn',
        description: 'Convertissez votre CV en profil LinkedIn optimisé avec titre, résumé et expérience formatée.',
      },
      jobAdapt: {
        title: 'Adaptation Emploi',
        description: "Collez une offre d'emploi et obtenez votre CV et lettre automatiquement adaptés aux exigences.",
      },
    },
    pricing: {
      title: 'Tarification Simple et Transparente',
      subtitle: 'Choisissez des services individuels ou abonnez-vous pour un accès complet.',
      free: {
        title: 'Aperçu Gratuit',
        price: '0€',
        description: 'Essayez avant d\'acheter',
        features: ['Aperçu complet du CV', 'Édition en direct', 'Sélection de thème', '1 génération test gratuite'],
        cta: 'Commencer Gratuit',
      },
      cvDownload: {
        title: 'Télécharger CV',
        price: '2€',
        description: 'Téléchargez votre CV professionnel',
        features: ['Export PDF', 'Format A4', 'Compatible ATS', 'Prêt à imprimer'],
        cta: 'Télécharger CV',
      },
      coverLetter: {
        title: 'Lettre de Motivation',
        price: '1€',
        description: 'Lettre de motivation professionnelle',
        features: ['Auto-générée depuis CV', 'Format professionnel', 'Personnalisable', 'Téléchargement PDF'],
        cta: 'Obtenir Lettre',
      },
      pack: {
        title: 'CV + Lettre',
        price: '3€',
        description: 'Pack candidature complet',
        features: ['Téléchargement CV', 'Lettre de motivation', 'Design assorti', 'Meilleur rapport'],
        cta: 'Obtenir Pack',
        popular: 'Populaire',
      },
      linkedin: {
        title: 'Conversion LinkedIn',
        price: '2€',
        description: 'Profil prêt pour LinkedIn',
        features: ['Générateur de titre', 'Texte de résumé', 'Format expérience', 'Copier-coller'],
        cta: 'Convertir en LinkedIn',
      },
      jobAdapt: {
        title: 'Adaptation Emploi',
        price: '5€',
        description: 'CV + Lettre pour un emploi spécifique',
        features: ['Coller offre', 'CV adapté', 'Lettre personnalisée', 'Optimisation mots-clés'],
        cta: 'Adapter à l\'Emploi',
      },
    },
    cvBuilder: {
      title: 'Créez Votre CV',
      preview: 'Aperçu',
      form: {
        personalInfo: 'Informations Personnelles',
        name: 'Nom Complet',
        jobTitle: 'Titre du Poste',
        email: 'Email',
        phone: 'Téléphone',
        address: 'Adresse',
        website: 'Site Web/Portfolio',
        photo: 'Photo de Profil',
        uploadPhoto: 'Télécharger Photo',
        changePhoto: 'Changer Photo',
        summary: 'Résumé Professionnel',
        summaryPlaceholder: 'Rédigez un bref résumé professionnel...',
        education: 'Formation',
        addEducation: 'Ajouter Formation',
        degree: 'Diplôme',
        institution: 'Établissement',
        year: 'Année',
        experience: 'Expérience Professionnelle',
        addExperience: 'Ajouter Expérience',
        position: 'Poste',
        company: 'Entreprise',
        period: 'Période',
        description: 'Description',
        skills: 'Compétences',
        addSkill: 'Ajouter Compétence',
        skillPlaceholder: 'Entrez une compétence et appuyez sur Entrée',
        languages: 'Langues',
        addLanguage: 'Ajouter Langue',
        language: 'Langue',
        level: 'Niveau',
        levels: {
          native: 'Natif',
          fluent: 'Courant',
          advanced: 'Avancé',
          intermediate: 'Intermédiaire',
          basic: 'Basique',
        },
      },
      theme: 'Thème du CV',
      themes: {
        blue: 'Bleu Professionnel',
        green: 'Vert Nature',
        creative: 'Violet Créatif',
      },
      download: 'Télécharger PDF',
      downloadCV: 'Télécharger CV',
      downloadCoverLetter: 'Télécharger Lettre',
      generateCoverLetter: 'Générer Lettre de Motivation',
      convertLinkedIn: 'Convertir en LinkedIn',
      adaptToJob: 'Adapter à l\'Offre',
      freeUsed: 'Génération gratuite utilisée',
      unlockWith: 'Débloquer avec paiement',
    },
    coverLetter: {
      title: 'Générateur de Lettre de Motivation',
      generated: 'Votre Lettre de Motivation',
      intro: 'Madame, Monsieur,',
      closing: 'Cordialement,',
    },
    linkedin: {
      title: 'Convertisseur Profil LinkedIn',
      headline: 'Titre LinkedIn',
      summary: 'Résumé Professionnel',
      experience: 'Format Expérience',
      copyAll: 'Tout Copier',
      copied: 'Copié !',
    },
    jobAdapt: {
      title: 'Adapter à l\'Offre d\'Emploi',
      paste: 'Coller la Description du Poste',
      placeholder: 'Collez le texte de l\'offre d\'emploi ici...',
      analyze: 'Analyser et Adapter',
      adaptedTitle: 'Titre CV Adapté',
      adaptedSummary: 'Résumé Adapté',
      adaptedLetter: 'Lettre Adaptée',
    },
    support: {
      title: 'Besoin d\'Aide ?',
      subtitle: 'Nous sommes là pour vous',
      contact: 'Contactez-nous',
      name: 'Votre Nom',
      email: 'Adresse Email',
      subject: 'Sujet',
      message: 'Votre Message',
      send: 'Envoyer',
      sent: 'Message Envoyé !',
      sentMessage: 'Nous vous répondrons sous 24 heures.',
      faq: 'Questions Fréquentes',
      faqs: [
        {
          q: 'Comment télécharger mon CV ?',
          a: 'Après avoir créé votre CV, cliquez simplement sur le bouton "Télécharger PDF". Un paiement unique de 2€ est requis pour débloquer le téléchargement.',
        },
        {
          q: 'Quels moyens de paiement acceptez-vous ?',
          a: 'Nous acceptons les paiements via Stripe et PayPal. Vos informations de paiement sont sécurisées et chiffrées.',
        },
        {
          q: 'Puis-je modifier mon CV après l\'avoir créé ?',
          a: 'Oui ! Vous pouvez modifier votre CV autant de fois que vous le souhaitez. Vos données sont sauvegardées dans votre navigateur.',
        },
        {
          q: 'Mon CV est-il compatible ATS ?',
          a: 'Oui, nos modèles de CV sont conçus pour être compatibles ATS. La mise en page épurée assure que votre CV peut être analysé par les logiciels de recrutement.',
        },
        {
          q: 'Comment fonctionne le générateur de lettre ?',
          a: 'La lettre est générée automatiquement à partir des informations de votre CV. Elle crée une lettre professionnelle adaptée à votre expérience.',
        },
      ],
    },
    footer: {
      description: 'Créez facilement des CV professionnels et des documents de candidature. Simple, abordable, multilingue.',
      quickLinks: 'Liens Rapides',
      legal: 'Légal',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions d\'Utilisation',
      copyright: `© ${new Date().getFullYear()} CVLY. Tous droits réservés.`,
    },
    common: {
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      close: 'Fermer',
      back: 'Retour',
      next: 'Suivant',
      required: 'Requis',
    },
    payment: {
      title: 'Finaliser Votre Achat',
      secure: 'Paiement sécurisé via',
      payWith: 'Payer avec',
      processing: 'Traitement...',
      success: 'Paiement Réussi !',
      successMessage: 'Votre téléchargement est maintenant débloqué.',
      testMode: 'Mode Test : Cliquez pour simuler le paiement',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      pricing: 'Precios',
      generateCV: 'Crear CV',
      support: 'Soporte',
      getStarted: 'Empezar',
      login: 'Iniciar sesión',
      logout: 'Cerrar sesión',
    },
    hero: {
      title: 'Crea Tu CV Profesional',
      titleHighlight: 'en Minutos',
      subtitle: 'Crea currículums impresionantes y compatibles con ATS. Simple, asequible y multilingüe.',
      cta: 'Crear Mi CV Gratis',
      ctaSecondary: 'Ver Ejemplos',
      stats: {
        cvs: 'CVs Creados',
        satisfaction: 'Satisfacción',
        languages: 'Idiomas',
      },
    },
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Todo lo que necesitas para conseguir tu trabajo soñado',
      cv: {
        title: 'Generador de CV',
        description: 'Crea un CV profesional de dos columnas con vista previa en vivo. Elige entre varios temas y exporta como PDF.',
      },
      coverLetter: {
        title: 'Carta de Presentación',
        description: 'Genera una carta personalizada basada en tu CV. Perfectamente formateada y lista para enviar.',
      },
      linkedin: {
        title: 'Perfil LinkedIn',
        description: 'Convierte tu CV en un perfil LinkedIn optimizado con título, resumen y experiencia formateada.',
      },
      jobAdapt: {
        title: 'Adaptación a Empleo',
        description: 'Pega una oferta de trabajo y obtén tu CV y carta automáticamente adaptados a los requisitos.',
      },
    },
    pricing: {
      title: 'Precios Simples y Transparentes',
      subtitle: 'Elige servicios individuales o suscríbete para acceso completo.',
      free: {
        title: 'Vista Previa Gratis',
        price: '0€',
        description: 'Prueba antes de comprar',
        features: ['Vista previa completa', 'Edición en vivo', 'Selección de tema', '1 generación de prueba'],
        cta: 'Empezar Gratis',
      },
      cvDownload: {
        title: 'Descargar CV',
        price: '2€',
        description: 'Descarga tu CV profesional',
        features: ['Exportar PDF', 'Formato A4', 'Compatible ATS', 'Listo para imprimir'],
        cta: 'Descargar CV',
      },
      coverLetter: {
        title: 'Carta de Presentación',
        price: '1€',
        description: 'Carta de presentación profesional',
        features: ['Auto-generada del CV', 'Formato profesional', 'Personalizable', 'Descarga PDF'],
        cta: 'Obtener Carta',
      },
      pack: {
        title: 'CV + Carta',
        price: '3€',
        description: 'Paquete de solicitud completo',
        features: ['Descarga CV', 'Carta de presentación', 'Diseño combinado', 'Mejor valor'],
        cta: 'Obtener Paquete',
        popular: 'Popular',
      },
      linkedin: {
        title: 'Conversión LinkedIn',
        price: '2€',
        description: 'Perfil listo para LinkedIn',
        features: ['Generador de título', 'Texto de resumen', 'Formato experiencia', 'Copiar y pegar'],
        cta: 'Convertir a LinkedIn',
      },
      jobAdapt: {
        title: 'Adaptación a Empleo',
        price: '5€',
        description: 'CV + Carta para trabajo específico',
        features: ['Pegar oferta', 'CV adaptado', 'Carta personalizada', 'Optimización palabras clave'],
        cta: 'Adaptar a Empleo',
      },
    },
    cvBuilder: {
      title: 'Crea Tu CV',
      preview: 'Vista Previa',
      form: {
        personalInfo: 'Información Personal',
        name: 'Nombre Completo',
        jobTitle: 'Título del Puesto',
        email: 'Email',
        phone: 'Teléfono',
        address: 'Dirección',
        website: 'Sitio Web/Portfolio',
        photo: 'Foto de Perfil',
        uploadPhoto: 'Subir Foto',
        changePhoto: 'Cambiar Foto',
        summary: 'Resumen Profesional',
        summaryPlaceholder: 'Escribe un breve resumen profesional...',
        education: 'Educación',
        addEducation: 'Añadir Educación',
        degree: 'Título',
        institution: 'Institución',
        year: 'Año',
        experience: 'Experiencia Laboral',
        addExperience: 'Añadir Experiencia',
        position: 'Puesto',
        company: 'Empresa',
        period: 'Período',
        description: 'Descripción',
        skills: 'Habilidades',
        addSkill: 'Añadir Habilidad',
        skillPlaceholder: 'Introduce habilidad y presiona Enter',
        languages: 'Idiomas',
        addLanguage: 'Añadir Idioma',
        language: 'Idioma',
        level: 'Nivel',
        levels: {
          native: 'Nativo',
          fluent: 'Fluido',
          advanced: 'Avanzado',
          intermediate: 'Intermedio',
          basic: 'Básico',
        },
      },
      theme: 'Tema del CV',
      themes: {
        blue: 'Azul Profesional',
        green: 'Verde Natural',
        creative: 'Púrpura Creativo',
      },
      download: 'Descargar PDF',
      downloadCV: 'Descargar CV',
      downloadCoverLetter: 'Descargar Carta',
      generateCoverLetter: 'Generar Carta de Presentación',
      convertLinkedIn: 'Convertir a LinkedIn',
      adaptToJob: 'Adaptar a Oferta',
      freeUsed: 'Generación gratuita usada',
      unlockWith: 'Desbloquear con pago',
    },
    coverLetter: {
      title: 'Generador de Carta de Presentación',
      generated: 'Tu Carta de Presentación',
      intro: 'Estimado/a responsable de selección,',
      closing: 'Atentamente,',
    },
    linkedin: {
      title: 'Conversor de Perfil LinkedIn',
      headline: 'Título LinkedIn',
      summary: 'Resumen Profesional',
      experience: 'Formato Experiencia',
      copyAll: 'Copiar Todo',
      copied: '¡Copiado!',
    },
    jobAdapt: {
      title: 'Adaptar a Oferta de Empleo',
      paste: 'Pegar Descripción del Puesto',
      placeholder: 'Pega el texto de la oferta de empleo aquí...',
      analyze: 'Analizar y Adaptar',
      adaptedTitle: 'Título CV Adaptado',
      adaptedSummary: 'Resumen Adaptado',
      adaptedLetter: 'Carta Adaptada',
    },
    support: {
      title: '¿Necesitas Ayuda?',
      subtitle: 'Estamos aquí para ti',
      contact: 'Contáctanos',
      name: 'Tu Nombre',
      email: 'Correo Electrónico',
      subject: 'Asunto',
      message: 'Tu Mensaje',
      send: 'Enviar Mensaje',
      sent: '¡Mensaje Enviado!',
      sentMessage: 'Te responderemos en 24 horas.',
      faq: 'Preguntas Frecuentes',
      faqs: [
        {
          q: '¿Cómo descargo mi CV?',
          a: 'Después de crear tu CV, simplemente haz clic en "Descargar PDF". Se requiere un pago único de 2€ para desbloquear la descarga.',
        },
        {
          q: '¿Qué métodos de pago aceptan?',
          a: 'Aceptamos pagos a través de Stripe y PayPal. Tu información de pago es segura y encriptada.',
        },
        {
          q: '¿Puedo editar mi CV después de crearlo?',
          a: '¡Sí! Puedes editar tu CV tantas veces como quieras. Tus datos se guardan en tu navegador.',
        },
        {
          q: '¿Mi CV es compatible con ATS?',
          a: 'Sí, nuestras plantillas de CV están diseñadas para ser compatibles con ATS. El diseño limpio asegura que tu CV pueda ser analizado por software de reclutamiento.',
        },
        {
          q: '¿Cómo funciona el generador de cartas?',
          a: 'La carta se genera automáticamente usando la información de tu CV. Crea una carta profesional adaptada a tu experiencia.',
        },
      ],
    },
    footer: {
      description: 'Crea fácilmente CVs profesionales y documentos de solicitud. Simple, asequible, multilingüe.',
      quickLinks: 'Enlaces Rápidos',
      legal: 'Legal',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
      copyright: `© ${new Date().getFullYear()} CVLY. Todos los derechos reservados.`,
    },
    common: {
      loading: 'Cargando...',
      error: 'Ocurrió un error',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      back: 'Volver',
      next: 'Siguiente',
      required: 'Requerido',
    },
    payment: {
      title: 'Completar Tu Compra',
      secure: 'Pago seguro vía',
      payWith: 'Pagar con',
      processing: 'Procesando...',
      success: '¡Pago Exitoso!',
      successMessage: 'Tu descarga está ahora desbloqueada.',
      testMode: 'Modo Prueba: Haz clic para simular pago',
    },
  },
};
